import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import moment from 'moment';
import { Icon, TimePicker } from 'antd';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import DataTables from 'components-desktop/DataTables';
import misc from 'misc/misc';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  scheduleVisits: PropTypes.array,
};

const defaultProps = {
  title: '',
  scheduleVisits: [],
};

@autobind
class TableConfirmVisits extends BaseComponent {
  constructor(props) {
    super(props);

    // Create group states to put them into this.state.
    const visitInGroupState = helpers.createGroupState(
      props.scheduleVisits,
      'visitIn',
      v => v.getString('visitInString') && moment(v.getString('visitInString'), 'HH:mm'),
    );
    const visitOutGroupState = helpers.createGroupState(
      props.scheduleVisits,
      'visitOut',
      v => v.getString('visitOutString') && moment(v.getString('visitOutString'), 'HH:mm'),
    );
    const visitDurationGroupState = helpers.createGroupState(
      props.scheduleVisits,
      'visitDuration',
      v => v.getString('visitDurationString'),
    );

    this.state = {
      savingSelectedVisits: false,
      ...visitInGroupState,
      ...visitOutGroupState,
      ...visitDurationGroupState,
    };

    // Initialize keepValues array.
    this.keepValues = [];
    props.scheduleVisits.forEach((visit) => {
      if (visit.getLong('visitIn')) {
        this.setKeepValue(`visitIn-${visit.getId()}`);
      }
      if (visit.getLong('visitOut')) {
        this.setKeepValue(`visitOut-${visit.getId()}`);
      }
    });
  }

  onVisitInChange(time, visitId) {
    this.setState({
      [`visitIn-${visitId}`]: time,
    });

    if (time) {
      this.setKeepValue(`visitIn-${visitId}`);
      this.calcAndSetDuration(visitId, time, this.state[`visitOut-${visitId}`]);
    }
  }

  onVisitOutChange(time, visitId) {
    this.setState({
      [`visitOut-${visitId}`]: time,
    });

    if (time) {
      this.setKeepValue(`visitOut-${visitId}`);
      this.calcAndSetDuration(visitId, this.state[`visitIn-${visitId}`], time);
    }
  }

  onRowSelect(record, selected, selectedRows) {
    const visitInKey = `visitIn-${record.id}`;
    const visitOutKey = `visitOut-${record.id}`;

    const visitIn = this.state[visitInKey];
    const visitOut = this.state[visitOutKey];

    if (selected) {
      this.setState((prevState, props) => ({
        [visitInKey]: !visitIn ? moment(record.fromTimeString, 'HH:mm') : prevState[visitInKey],
        [visitOutKey]: !visitOut ? moment(record.toTimeString, 'HH:mm') : prevState[visitOutKey],
      }), () => {
        this.calcAndSetDuration(record.id, this.state[visitInKey], this.state[visitOutKey]);
      });
    } else {
      this.setState((prevState, props) => ({
        [visitInKey]: !this.isKeepValue(visitInKey) ? null : prevState[visitInKey],
        [visitOutKey]: !this.isKeepValue(visitOutKey) ? null : prevState[visitOutKey],
      }), () => {
        this.calcAndSetDuration(record.id, this.state[visitInKey], this.state[visitOutKey]);
      });
    }
  }

  onRowSelectAll(selected, selectedRows, changeRows) {
    changeRows.forEach((changeRow) => {
      this.onRowSelect(changeRow, selected, selectedRows);
    });
  }

  async onSaveAllClick() {
    const selectedVisitIds = this.dataTables.getSelectedRowKeys();
    if (selectedVisitIds.length > 0) {
      try {
        this.setState({
          savingSelectedVisits: true,
        });

        const savedVisitIds = [];
        selectedVisitIds.forEach(async (visitId) => {
        });

        for (let i = 0; i < selectedVisitIds.length; i++) {
          const visitId = selectedVisitIds[i];
          // eslint-disable-next-line
          await this.onSaveVisitClick(visitId, false);

          // Will uncheck this row.
          savedVisitIds.push(visitId);
        }

        // Uncheck saved rows.
        const origSelections = this.dataTables.getSelectedRowKeys();
        this.dataTables.setSelectedRowKeys(origSelections.filter(e => !savedVisitIds.includes(e)));

        this.notifySuccess('Success', `${selectedVisitIds.length} visits confirmed.`);
      } finally {
        this.setState({
          savingSelectedVisits: false,
        });
      }
    } else {
      this.notifyWarning('No visit is selected.');
    }
  }

  async onSaveAllAndNextClick() {
    await this.onSaveAllClick();

    // Move to next page.
    this.dataTables.setCurrrentPage(this.dataTables.getCurrentPage() + 1);
  }

  async onSaveVisitClick(visitId, showSuccessMsg = true) {
    const visitIn = this.state[`visitIn-${visitId}`];
    const visitOut = this.state[`visitOut-${visitId}`];
    const params = {
      id: visitId,
      visitIn: misc.toTimeString(visitIn),
      visitOut: misc.toTimeString(visitOut),
    };
    await this.callService('/schedule_visit/update', params);

    if (visitIn) {
      this.setKeepValue(`visitIn-${visitId}`);
    }
    if (visitOut) {
      this.setKeepValue(`visitOut-${visitId}`);
    }

    if (showSuccessMsg) {
      this.notifySuccess('Success', `Visit confirmed.`);
    }
  }

  // Calculates and sets the "Duration".
  calcAndSetDuration(visitId, visitIn, visitOut) {
    if (visitIn && visitOut) {
      const duration = moment.duration(visitOut.diff(visitIn));
      this.setState({
        [`visitDuration-${visitId}`]: Math.floor(duration.asHours())
          + moment.utc(duration.asMilliseconds()).format(':mm'),
      });
    } else {
      this.setState({
        [`visitDuration-${visitId}`]: '',
      });
    }
  }

  setKeepValue(inputIdentifier) {
    if (inputIdentifier && !this.keepValues.includes(inputIdentifier)) {
      this.keepValues.push(inputIdentifier);
    }
  }

  isKeepValue(inputIndentifier) {
    return this.keepValues.includes(inputIndentifier);
  }

  rowClassName(record, index) {
    const selectedRowKeys = this.dataTables?.getSelectedRowKeys();
    return selectedRowKeys && selectedRowKeys.includes(record.id) ? 'highlight' : undefined;
  }

  createColumns() {
    return [{
      title: 'Id',
      dataIndex: 'id',
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'ascend',
    }, {
      title: 'Date',
      dataIndex: 'dateUsString',
      sorter: (a, b) => a.date - b.date,
    }, {
      title: 'Company',
      dataIndex: 'companyName',
      sorter: (a, b) => a.companyName?.localeCompare(b.companyName),
    }, {
      title: 'Service Code',
      dataIndex: 'serviceCode',
      sorter: (a, b) => a.serviceCode?.localeCompare(b.serviceCode),
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Caregiver',
      dataIndex: 'caregiverName',
      sorter: (a, b) => a.caregiverName?.localeCompare(b.caregiverName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/caregivers/caregiver_profile?id=${record.caregiverId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Coordinator',
      dataIndex: 'patientCoordinator',
      sorter: (a, b) => a.patientCoordinator?.localeCompare(b.patientCoordinator),
    }, {
      title: 'Team',
      dataIndex: 'patientTeam',
      sorter: (a, b) => a.patientTeam?.localeCompare(b.patientTeam),
    }, {
      title: 'Sch. Time - In',
      dataIndex: 'fromTimeString',
      sorter: (a, b) => a.fromTime - b.fromTime,
    }, {
      title: 'Sch. Time - Out',
      dataIndex: 'toTimeString',
      sorter: (a, b) => a.toTime - b.toTime,
    }, {
      title: 'Visit Time - In',
      render: (text, record) => (
        <TimePicker
          style={{
            width: '100%',
          }}
          placeholder="hh:mm"
          value={this.state[`visitIn-${record.id}`]}
          format="HH:mm"
          defaultOpenValue={moment('00:00:00', 'HH:mm:ss')}
          onChange={(time, timeString) => this.onVisitInChange(time, record.id)}
        />
      ),
    }, {
      title: 'Visit Time - Out',
      render: (text, record) => (
        <TimePicker
          style={{
            width: '100%',
          }}
          placeholder="hh:mm"
          value={this.state[`visitOut-${record.id}`]}
          format="HH:mm"
          defaultOpenValue={moment('00:00:00', 'HH:mm:ss')}
          onChange={(time, timeString) => this.onVisitOutChange(time, record.id)}
        />
      ),
    }, {
      title: 'Duration',
      dataIndex: 'visitDurationString',
      sorter: (a, b) => a.visitDurationString?.localeCompare(b.visitDurationString),
      render: (text, record) => (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          color="textPrimaryDark"
          fontSize="12px"
        >
          {this.state[`visitDuration-${record.id}`]}
        </styledComp.Span>
      ),
    }, {
      title: 'Confirm Time',
      dataIndex: 'confirmTimeUsString',
      sorter: (a, b) => a.confirmTime - b.confirmTime,
    }, {
      title: 'Actions',
      render: (text, record) => !record.invoiceId && (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          fontSize="12px"
          onClick={() => this.onSaveVisitClick(record.id)}
        >
          Save
        </styledComp.A>
      ),
    }];
  }

  createDataSource(scheduleVisits) {
    const dataSource = [];
    scheduleVisits.forEach((visit, index) => {
      dataSource.push({
        key: visit.getId(),
        id: visit.getId(),
        date: visit.getLong('date'),
        dateUsString: visit.getString('dateUsString'),
        companyName: visit.getString('companyName'),
        serviceCode: visit.getRemoteObject('service')?.getString('displayCode'),
        patientId: visit.getLong('patientId'),
        patientName: visit.getString('patientName'),
        caregiverId: visit.getLong('caregiverId'),
        caregiverName: visit.getString('caregiverName'),
        patientCoordinator: visit.getString('patientCoordinator'),
        patientTeam: visit.getString('patientTeam'),
        fromTime: visit.getLong('fromTime'),
        fromTimeString: visit.getString('fromTimeString'),
        toTime: visit.getLong('toTime'),
        toTimeString: visit.getString('toTimeString'),
        visitIn: visit.getLong('visitIn'),
        visitInString: visit.getString('visitInString'),
        visitOut: visit.getLong('visitOut'),
        visitOutString: visit.getString('visitOutString'),
        visitDurationString: visit.getString('visitDurationString'),
        confirmTime: visit.getLong('confirmTime'),
        confirmTimeUsString: visit.getString('confirmTimeUsString'),
        invoiceId: visit.getLong('invoiceId'),
      });
    });
    return dataSource;
  }

  renderFooter() {
    const { savingSelectedVisits } = this.state;

    return (
      <FlexRowContainer
        width="match_parent"
        height="wrap_content"
        padding="20px 20px"
        justifyContent="center"
        backgroundColor="containerGrayBackground"
      >
        <CustomButton
          width="wrap_content"
          height="32px"
          margin="0 4px 0 0"
          backgroundColor="purple_wisteria"
          fontSize="14px"
          type="primary"
          loading={savingSelectedVisits}
          onClick={this.onSaveAllClick}
        >
          SAVE ALL
        </CustomButton>

        <CustomButton
          width="wrap_content"
          height="32px"
          backgroundColor="purple_wisteria"
          fontSize="14px"
          type="primary"
          loading={savingSelectedVisits}
          onClick={this.onSaveAllAndNextClick}
        >
          SAVE ALL & NEXT
          <Icon type="right" />
        </CustomButton>
      </FlexRowContainer>
    );
  }

  renderContent() {
    const { scheduleVisits, title, ...layoutProps } = this.props;

    const rowSelection = {
      getCheckboxProps: record => ({
        disabled: !!record.invoiceId,
      }),
      onSelect: this.onRowSelect,
      onSelectAll: this.onRowSelectAll,
    };

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="purple_wisteria"
        fontSize="12px"
        size="small"
        title={title}
        icon="check-square"
        withColumnFilter={false}
        selectable
        rowSelection={rowSelection}
        rowClassName={this.rowClassName}
        columns={this.createColumns()}
        dataSource={this.createDataSource(scheduleVisits)}
        footer={this.renderFooter()}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableConfirmVisits.propTypes = propTypes;
TableConfirmVisits.defaultProps = defaultProps;

export default TableConfirmVisits;
